import { createContext, useContext, useState, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { apiClient } from "../apiClient";

interface UploadResult {
  righe_importate?: number;
  filename?: string;
  message?: string;
  success?: boolean;
}

export interface ExportCounts {
  contatti: number;
  small_leads: number;
  startup: number;
}

interface ExportResult {
  success: boolean;
  message: string;
  filename?: string;
  counts?: ExportCounts;
}

interface ImportContextType {
  uploadFile: (file: File) => Promise<UploadResult>;
  downloadBackup: () => Promise<ExportResult>;
  loading: boolean;
  exportLoading: boolean;
  result: UploadResult | null;
  exportResult: ExportResult | null;
  resetResult: () => void;
  resetExportResult: () => void;
}

const ImportContext = createContext<ImportContextType | undefined>(undefined);

export const ImportProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);

  const uploadFile = async (file: File) => {
    if (!isAuthenticated) throw new Error("Devi essere loggato per importare un file!");
    if (!isAdmin) throw new Error("Solo gli admin possono importare dati");
    setLoading(true);
    setResult(null);

    const buildFormData = () => {
      const fd = new FormData();
      fd.append("file", file);
      return fd;
    };

    try {
      const [resContatti, resSmallLeads, resStartup] = await Promise.all([
        apiClient.post("import-contatti/", buildFormData(), {
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        }),
        apiClient.post("import-small-leads/", buildFormData(), {
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        }),
        apiClient.post("import-startup/", buildFormData(), {
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        }),
      ]);

      const dataContatti = resContatti.data as UploadResult;
      const dataSmall = resSmallLeads.data as UploadResult;
      const dataStartup = resStartup.data as UploadResult;

      const totaleRighe =
        (dataContatti.righe_importate ?? 0) +
        (dataSmall.righe_importate ?? 0) +
        (dataStartup.righe_importate ?? 0);

      const finalResult = {
        filename: file.name,
        righe_importate: totaleRighe,
        message: `LinkedIn: ${dataContatti.righe_importate ?? 0} | Small Leads: ${dataSmall.righe_importate ?? 0} | Startup: ${dataStartup.righe_importate ?? 0}`,
        success:
          (dataContatti.success ?? false) ||
          (dataSmall.success ?? false) ||
          (dataStartup.success ?? false) ||
          totaleRighe > 0,
      };

      setResult(finalResult);
      return finalResult;
    } catch (error: any) {
      const status = error?.response?.status;
      let message =
        error?.response?.data?.detail ?? error?.message ?? "Errore sconosciuto durante l'importazione multipla";
      if (status === 413) {
        message =
          "File troppo grande per il server (limite 100 MB). Riduci il file o importa un foglio alla volta.";
      }
      const errResult: UploadResult = {
        success: false,
        message,
      };
      setResult(errResult);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetResult = () => setResult(null);
  const resetExportResult = () => setExportResult(null);

  const downloadBackup = async (): Promise<ExportResult> => {
    if (!isAuthenticated) throw new Error("Devi essere loggato per esportare i dati");
    if (!isAdmin) throw new Error("Solo gli admin possono esportare dati");

    setExportLoading(true);
    setExportResult(null);

    try {
      const res = await apiClient.get("export-leads/", {
        responseType: "blob",
      });

      let counts: ExportCounts | undefined;
      const countsHeader = res.headers["x-export-counts"];
      if (typeof countsHeader === "string") {
        try {
          counts = JSON.parse(countsHeader) as ExportCounts;
        } catch {
          counts = undefined;
        }
      }

      const blob = res.data as Blob;
      const filename =
        res.headers["content-disposition"]?.match(/filename="?([^";]+)"?/)?.[1] ??
        `jevis_backup_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const message = counts
        ? `LinkedIn: ${counts.contatti} | Small Leads: ${counts.small_leads} | Startup: ${counts.startup}`
        : "Download completato";

      const finalResult: ExportResult = {
        success: true,
        message,
        filename,
        counts,
      };
      setExportResult(finalResult);
      return finalResult;
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: Blob | { detail?: string } } };
      let message = "Errore durante l'export";
      if (err.response?.status === 403) {
        message = "Solo gli admin possono esportare dati";
      } else if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text) as { detail?: string };
          message = parsed.detail ?? message;
        } catch {
          /* keep default */
        }
      } else if (err.response?.data && typeof err.response.data === "object" && "detail" in err.response.data) {
        message = String((err.response.data as { detail?: string }).detail ?? message);
      }

      const errResult: ExportResult = { success: false, message };
      setExportResult(errResult);
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <ImportContext.Provider
      value={{
        uploadFile,
        downloadBackup,
        loading,
        exportLoading,
        result,
        exportResult,
        resetResult,
        resetExportResult,
      }}
    >
      {children}
    </ImportContext.Provider>
  );
};

export const useImport = () => {
  const context = useContext(ImportContext);
  if (!context) throw new Error("useImport deve essere usato dentro ImportProvider");
  return context;
};
